<template>
  <WorkflowNode can-delete class="rules" data-test="workflow-custom-rule" @delete="$emit('delete')">
    <template #icon>
      <WdsSpinner v-if="loading" indeterminate />
      <WdsIcon v-else thin name="filter" weight="thin" :size="20" />
    </template>
    <template #title>
      <WdsText large>{{ $t('Custom rule') }}</WdsText>
    </template>
    <div v-if="!loading" class="tw-mt-3">
      <TransitionGroup name="list" :css="allRules.length > 1">
        <div v-for="(rule, index) in allRules" :key="rule.id" class="tw-mb-4">
          <WdsStack gap-sm>
            <WdsSelect
              v-model="rule.variable"
              :placeholder="$t('Select')"
              class="tw-flex-1 tw-min-w-0"
              :list="variableList"
              searchable
              name="workflow-custom-rule-variable-select"
              @select-option="resetRuleFieldOption(rule, $event)"
              @load-more="loadMore"
            >
              <template #option="{ option }">
                <div
                  v-if="option.type === ItemTypes.Title"
                  class="title-field-option tw-w-full"
                  @mousedown.stop
                >
                  <WdsStack class="option-label" gap-sm justify-space-between align-center>
                    <WdsText secondary>
                      {{ option.label }}
                    </WdsText>
                    <WdsTooltip :text="option.tooltip" global>
                      <WdsText tertiary>
                        <WdsIcon :size="16" name="help-filled" />
                      </WdsText>
                    </WdsTooltip>
                  </WdsStack>
                </div>
                <div
                  v-else-if="option.type === ItemTypes.CreateCustomField"
                  class="title-field-option new-field-option"
                  @mousedown.stop="addNewCustomField = true"
                >
                  <WdsStack class="option-label" gap-sm>
                    <WdsIcon :size="20" name="plus" />
                    {{ option.label }}
                  </WdsStack>
                </div>
                <WdsLabel v-else-if="option.type === ItemTypes.Variable" xs subtle>
                  {{ option.label }}
                </WdsLabel>
                <span v-else class="single-line" :title="option.label">
                  {{ option.label }}
                </span>
              </template>
            </WdsSelect>
            <WdsSelect
              :start-selected="rule.operator"
              :disabled="!rule.variable?.id"
              placeholder="-"
              class="tw-flex-0 tw-min-w-0 operator"
              :list="conditionOptions.filter((c) => rule.variable.operators.includes(c.value))"
              name="workflow-custom-rule-operator-select"
              @select-option="rule.operator = $event.value"
            />
            <WdsSelect
              v-if="rule.variable?.__typename === 'WorkflowVariableOptionType'"
              :start-selected="rule.value"
              class="tw-flex-1 tw-min-w-0"
              :disabled="!rule.variable?.id"
              placeholder="-"
              :list="
                rule.variable?.options.map((option) => ({
                  value: option.value,
                  label: option.label,
                }))
              "
              @select-option="rule.value = $event.value"
            />
            <WdsInput
              v-else-if="
                rule.variable?.__typename === 'WorkflowVariableStringType' &&
                rule.operator != WorkflowVariableOperator.Changed
              "
              v-model="rule.value"
              :disabled="!rule.variable?.id"
              placeholder="-"
              data-test="workflow-custom-rule-value-input"
            />
            <WdsInput v-else disabled placeholder="-" />
            <WdsButtonIcon sm transparent-background class="tw-flex-0" @click="removeRule(rule)">
              <WdsIcon :size="14" color="#B2B2BF" name="close" />
            </WdsButtonIcon>
          </WdsStack>
          <transition name="fade">
            <WdsDivider v-if="index < rules.length - 1" align-left class="tw-my-5 divider-text">
              <WdsText primary>{{ $t('And') }}</WdsText>
            </WdsDivider>
          </transition>
        </div>
        <WdsButton key="add-condition" tertiary full-width :disabled="loading" @click="addRule">
          {{ $t('Add conditional') }}
        </WdsButton>
      </TransitionGroup>
    </div>
  </WorkflowNode>
  <teleport to="body">
    <AddCustomField
      :show="addNewCustomField"
      :account-id="account"
      :visibility="CustomFieldVisibility.Internal"
      :applies-to="appliesTo"
      @cancel="addNewCustomField = false"
      @success="createdNewCustomField"
    />
  </teleport>
</template>

<script setup lang="ts">
import { computed, Ref, ref } from 'vue'
import {
  WdsButton,
  WdsButtonIcon,
  WdsDivider,
  WdsIcon,
  WdsInput,
  WdsLabel,
  WdsSelect,
  WdsSpinner,
  WdsStack,
  WdsText,
  WdsTooltip,
} from '@worksome/wds-components'
import { useQuery } from '@vue/apollo-composable'
import { useVModel } from '@vueuse/core'
import WorkflowNode from '@/components/Workflows/Pages/Editor/Nodes/NodeComponent.vue'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { flat, uuid } from '@/utils/tools'
import { ListItem, WorkflowVariableRule } from '@/components/Workflows/Pages/Workflow/workflow'
import {
  ApprovalTrigger,
  CustomFieldVisibility,
  TypeSupportingCustomFieldValues,
  WorkflowVariable,
  WorkflowVariableOperator,
  WorkflowVariableOrigins,
} from '@/gql/graphql'
import {
  WorkflowVariableFragment,
  WorkflowVariablesQuery,
} from '@/components/Workflows/Pages/Workflow/graphql/queries'
import AddCustomField from '@/components/Fields/AddCustomField.vue'
import { useFragment } from '@/gql'

const emit = defineEmits(['delete', 'update:modelValue'])

const props = withDefaults(
  defineProps<{
    modelValue?: WorkflowVariableRule[]
    trigger: ApprovalTrigger
  }>(),
  {
    modelValue: () => [],
  },
)

// ================= DATA LOGIC =================
const conditionOptions: Ref<ListItem[]> = ref([
  {
    label: $t('Equals'),
    value: WorkflowVariableOperator.Equals,
  },
  {
    label: $t('Not equals'),
    value: WorkflowVariableOperator.NotEquals,
  },
  {
    label: $t('Contains'),
    value: WorkflowVariableOperator.Contains,
  },
  {
    label: $t('Does not contain'),
    value: WorkflowVariableOperator.DoesNotContain,
  },
  {
    label: $t('Less than'),
    value: WorkflowVariableOperator.Less,
  },
  {
    label: $t('Greater than'),
    value: WorkflowVariableOperator.Greater,
  },
  {
    label: $t('Greater or equals'),
    value: WorkflowVariableOperator.GreaterOrEquals,
  },
  {
    label: $t('Less or equals'),
    value: WorkflowVariableOperator.LessOrEquals,
  },
  {
    label: $t('Changed'),
    value: WorkflowVariableOperator.Changed,
  },
])
enum ItemTypes {
  Title,
  Variable,
  Field,
  CreateCustomField,
}

interface ListItemType extends ListItem {
  id: string
  type: ItemTypes
  tooltip?: string
}

type WorkflowVariableListItem = WorkflowVariable & ListItemType

const TitleCustomFields: ListItemType = {
  label: $t('Custom fields'),
  value: 'custom_field',
  tooltip: $t('Specify and use additional information to create conditions for your workflow'),
  id: uuid(),
  type: ItemTypes.Title,
}

const newCustomFieldSelectOption: ListItemType = {
  label: $t('New custom field'),
  value: 'new_custom_field',
  id: uuid(),
  type: ItemTypes.CreateCustomField,
}

const TitleField: ListItemType = {
  value: 'system_fields',
  label: $t('System variables'),
  tooltip: $t('Use internal data to create conditions for your workflow'),
  type: ItemTypes.Title,
  id: uuid(),
}

const appliesTo = TypeSupportingCustomFieldValues.Job
const account = currentAccount?.globalId
const addNewCustomField = ref(false)

const rules: Ref<WorkflowVariableRule[]> = useVModel(props, 'modelValue', emit, {
  passive: true,
  deep: true,
})

const resetRuleFieldOption = (rule: WorkflowVariableRule, variable: WorkflowVariable | null) => {
  Object.assign(rule, { value: '', operator: '', variable })
}

function createRule() {
  return {
    id: uuid(),
    value: '',
    operator: '',
    fieldId: '',
    variable: {
      operators: [],
      options: [],
    },
  }
}

function addRule() {
  // @ts-ignore
  rules.value.push(createRule())
}

function removeRule(rule: WorkflowVariableRule) {
  rules.value = rules.value.filter((r) => r.id !== rule.id)
}

const allRules = computed(() => {
  if (rules.value.length === 0) {
    addRule() // Adding a rule prevents double clicking of the add conditional button
  }

  return rules.value.map((rule) =>
    Object.assign(rule, { variable: useFragment(WorkflowVariableFragment, rule.variable) }),
  )
})

// ================= NETWORK LOGIC ================
const { result, loading, fetchMore, refetch } = useQuery(
  WorkflowVariablesQuery,
  {
    accounts: [currentAccount.globalId],
    appliesTo: props.trigger,
    page: 1,
  },
  {
    clientId: 'publicApiCustomFields',
  },
)

const createdNewCustomField = () => {
  addNewCustomField.value = false
  refetch()
}

const loadMore = () => {
  if (loading.value) return
  const { hasMorePages, currentPage } = flat(result.value ?? {}) ?? {}
  if (!hasMorePages) return
  fetchMore({
    variables: {
      page: currentPage + 1,
    },
  })
}

let oldState: Partial<WorkflowVariableListItem>[] = [TitleField]
  .concat([TitleCustomFields])
  .concat([newCustomFieldSelectOption])
const variableList = computed<Partial<WorkflowVariableListItem>[]>(() => {
  if (loading.value) {
    return oldState
  }

  const workflowVariablesTyped =
    result.value?.workflowVariables.data
      .map((workflowVariable) => ({
        ...useFragment(WorkflowVariableFragment, workflowVariable),
      }))
      .map((workflowVariable) => ({
        ...workflowVariable,
        type:
          workflowVariable.originMeta === WorkflowVariableOrigins.SystemVariable
            ? ItemTypes.Variable
            : ItemTypes.Field,
      })) ?? []

  const systemVariables = workflowVariablesTyped.filter(
    (variable) => variable.originMeta === WorkflowVariableOrigins.SystemVariable,
  )
  const customVariables = workflowVariablesTyped.filter(
    (variable) => variable.originMeta === WorkflowVariableOrigins.CustomVariable,
  )

  oldState = [TitleField]
    .concat(systemVariables)
    .concat([TitleCustomFields])
    .concat(customVariables)
    .concat([newCustomFieldSelectOption])
  oldState = [...new Map(oldState.map((item) => [item.id, item])).values()]

  return oldState
})
</script>

<style lang="scss" scoped>
.rules {
  .divider-text {
    &::v-deep(.wds-text-tertiary) {
      font-size: 12px;
    }
  }

  .operator {
    flex-basis: 5.6rem;
  }

  :deep(.wds-input-wrapper) {
    flex: 1;
    min-width: 0;
    .wds-input {
      min-width: 0;
    }
  }

  :deep(.wds-select-list-container .wds-select-list) {
    padding-bottom: 0;
  }

  :deep(.wds-select-list) {
    .wds-select-option {
      &:has(.title-field-option) {
        &:not(:first-child) {
          border-radius: 0;
          border-top: #f0f0f0 1px solid;
        }
        margin: 0;
        padding: 0;
        &:hover {
          background-color: white;
        }
      }
      &:has(.new-field-option) {
        position: sticky;
        bottom: 0;
        background: white;
        &:hover {
          background-color: rgb(248 248 248 / var(--tw-bg-opacity));
        }
      }
    }
  }

  .single-line {
    word-break: keep-all;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    max-width: 100%;
  }

  .new-field-option {
    margin: 0;
    .option-label {
      &:hover {
        background-color: inherit;
      }
    }
  }

  .title-field-option {
    width: 366px;
    padding: 0.5rem 1rem 0.75rem;
    cursor: auto;
    &.new-field-option {
      cursor: pointer;
      padding: 0.3rem 0.5rem;
    }
  }

  .list-move, /* apply transition to moving elements */
  .list-leave-active,
  .list-enter-active {
    transition: all 0.5s ease;
  }

  .list-enter-from,
  .list-leave-to {
    opacity: 0;
    transform: translateX(30px);
  }

  /* ensure leaving items are taken out of layout flow so that moving
     animations can be calculated correctly. */
  .list-leave-active {
    position: absolute;
    width: 100%;
  }
}
</style>
